import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  Unsubscribe,
  where,
  writeBatch,
  DocumentSnapshot
} from 'firebase/firestore';
import { db, auth } from './firebase';
import {
  EmailLog,
  EmailSettings,
  EmailTemplateType,
  Reservation,
  SiteSettings,
  CustomerMessage,
  UserProfile
} from '../types';
import {
  DEFAULT_EMAIL_SETTINGS,
  getBaseLayout,
  renderBeingPreparedEmail,
  renderCollectedEmail,
  renderCollectionDateUpdatedEmail,
  renderContactAcknowledgementEmail,
  renderReadyForCollectionEmail,
  renderReservationCancelledEmail,
  renderReservationCreatedEmail,
  renderTestEmail,
  renderVolunteerReplyEmail,
  renderVolunteerNewOrderEmail,
  renderVolunteerOrderUpdateEmail,
  renderVolunteerNewMessageEmail
} from './emailTemplates';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';
import { isUserEligibleAndSubscribed } from '../utils/permissions';
import {
  fetchCustomTemplate,
  interpolateVariables,
  convertBodyToHtml
} from './templateStorage';
import { getCustomerReference } from '../utils/referenceGenerator';

const SETTINGS_DOC = 'email_settings/config';
const LOGS_COLLECTION = 'email_logs';
export const DEFAULT_RETENTION_DAYS = 30;

/**
 * Subscribe to Admin Email Settings in Firestore
 */
export function subscribeEmailSettings(
  onData: (settings: EmailSettings) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const docRef = doc(db, SETTINGS_DOC);
  return onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        onData({ ...DEFAULT_EMAIL_SETTINGS, ...(snapshot.data() as EmailSettings) });
      } else {
        onData(DEFAULT_EMAIL_SETTINGS);
      }
    },
    (err) => {
      console.warn('Note: Email settings listener encountered restricted or offline state:', err?.message || err);
      onData(DEFAULT_EMAIL_SETTINGS);
      if (onError) onError(err);
    }
  );
}

/**
 * Update Admin Email Settings in Firestore
 */
export async function updateEmailSettings(
  updates: Partial<EmailSettings>
): Promise<void> {
  const docRef = doc(db, SETTINGS_DOC);
  const cleanUpdates = sanitizeFirestoreData(updates);
  await setDoc(docRef, cleanUpdates, { merge: true });
}

/**
 * Subscribe to Email Audit Logs in Firestore (Lightweight Free-Tier Optimized)
 * Fetches only the newest records (default 25) with ordered limit to avoid collection scans.
 */
export function subscribeEmailLogs(
  onData: (logs: EmailLog[]) => void,
  onError?: (error: Error) => void,
  limitCount = 25
): Unsubscribe {
  const logsRef = collection(db, LOGS_COLLECTION);
  const q = query(logsRef, orderBy('sentAt', 'desc'), limit(limitCount));

  return onSnapshot(
    q,
    (snapshot) => {
      const logs: EmailLog[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          sentAt: data.sentAt || new Date().toISOString(),
          recipientEmail: data.recipientEmail || data.recipient || data.toEmail || '',
          recipientName: data.recipientName || '',
          templateUsed: data.templateUsed || data.templateType || 'reservation_created',
          subject: data.subject || 'Uniform Loop Notification',
          status: (data.status === 'Sent' || data.status === 'Delivered' || data.deliveryStatus === 'sent')
            ? 'Sent'
            : (data.status === 'Queued' ? 'Queued' : 'Failed'),
          sentBy: data.sentBy || data.volunteerName || 'System',
          reservationId: data.reservationId || data.relatedReservationId,
          reservationCode: data.reservationCode || data.relatedReservationCode,
          hasAttachment: Boolean(data.hasAttachment),
          errorCode: data.errorCode || data.error,
          errorMessage: data.errorMessage || data.errorDetails,
          // Compatibility fields
          recipient: data.recipientEmail || data.recipient || data.toEmail || '',
          toEmail: data.recipientEmail || data.recipient || data.toEmail || '',
          templateType: data.templateUsed || data.templateType || 'reservation_created',
          deliveryStatus: (data.status === 'Sent' || data.status === 'Delivered' || data.deliveryStatus === 'sent') ? 'sent' : 'failed',
          relatedReservationCode: data.reservationCode || data.relatedReservationCode,
          relatedReservationId: data.reservationId || data.relatedReservationId
        };
      });

      onData(logs);
    },
    (err) => {
      console.warn('Note: Email logs listener encountered restricted or offline state:', err?.message || err);
      onData([]);
      if (onError) onError(err);
    }
  );
}

export interface FetchLogsFilterOptions {
  pageSize?: number;
  lastDoc?: DocumentSnapshot | null;
  statusFilter?: string; // 'all' | 'Sent' | 'Failed'
  searchQuery?: string;
  templateFilter?: string;
  startDate?: string;
  endDate?: string;
}

export interface PaginatedLogsResult {
  logs: EmailLog[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
  totalLoaded: number;
}

/**
 * On-demand Paginated Fetch for Email Audit History (Minimises Firestore Reads)
 */
export async function fetchEmailLogsPage(
  options: FetchLogsFilterOptions = {}
): Promise<PaginatedLogsResult> {
  const pageSize = options.pageSize || 25;
  const logsRef = collection(db, LOGS_COLLECTION);

  let q = query(logsRef, orderBy('sentAt', 'desc'), limit(pageSize + 1));

  if (options.lastDoc) {
    q = query(logsRef, orderBy('sentAt', 'desc'), startAfter(options.lastDoc), limit(pageSize + 1));
  }

  const snapshot = await getDocs(q);
  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;
  const nextLastDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

  let logs: EmailLog[] = pageDocs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      sentAt: data.sentAt || new Date().toISOString(),
      recipientEmail: data.recipientEmail || data.recipient || data.toEmail || '',
      recipientName: data.recipientName || '',
      templateUsed: data.templateUsed || data.templateType || 'reservation_created',
      subject: data.subject || '',
      status: (data.status === 'Sent' || data.status === 'Delivered' || data.deliveryStatus === 'sent')
        ? 'Sent'
        : (data.status === 'Queued' ? 'Queued' : 'Failed'),
      sentBy: data.sentBy || data.volunteerName || 'System',
      reservationId: data.reservationId || data.relatedReservationId,
      reservationCode: data.reservationCode || data.relatedReservationCode,
      hasAttachment: Boolean(data.hasAttachment),
      errorCode: data.errorCode || data.error,
      errorMessage: data.errorMessage || data.errorDetails,
      // Compatibility fields
      recipient: data.recipientEmail || data.recipient || data.toEmail || '',
      toEmail: data.recipientEmail || data.recipient || data.toEmail || '',
      templateType: data.templateUsed || data.templateType || 'reservation_created',
      deliveryStatus: (data.status === 'Sent' || data.status === 'Delivered' || data.deliveryStatus === 'sent') ? 'sent' : 'failed',
      relatedReservationCode: data.reservationCode || data.relatedReservationCode,
      relatedReservationId: data.reservationId || data.relatedReservationId
    };
  });

  // Client-side filtering when specific parameters are passed
  if (options.statusFilter && options.statusFilter !== 'all') {
    logs = logs.filter((l) => l.status.toLowerCase() === options.statusFilter?.toLowerCase());
  }

  if (options.searchQuery && options.searchQuery.trim()) {
    const term = options.searchQuery.toLowerCase().trim();
    logs = logs.filter((l) =>
      l.recipientEmail.toLowerCase().includes(term) ||
      (l.recipientName && l.recipientName.toLowerCase().includes(term)) ||
      (l.reservationCode && l.reservationCode.toLowerCase().includes(term)) ||
      l.subject.toLowerCase().includes(term)
    );
  }

  return {
    logs,
    lastDoc: nextLastDoc,
    hasMore,
    totalLoaded: logs.length
  };
}

/**
 * Automatic Cleanup Process: Deletes email audit logs older than retention period (default 30 days)
 * Minimises Firestore storage and keeps database lightweight within free tier allowances.
 */
export async function cleanupOldEmailLogs(
  retentionDays = DEFAULT_RETENTION_DAYS
): Promise<{ deletedCount: number; cutoffDate: string }> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - retentionDays);
  const cutoffIso = cutoff.toISOString();

  const logsRef = collection(db, LOGS_COLLECTION);
  const oldLogsQuery = query(
    logsRef,
    where('sentAt', '<', cutoffIso),
    limit(400) // Keep well within Firestore 500-op batch limits
  );

  const snapshot = await getDocs(oldLogsQuery);
  if (snapshot.empty) {
    return { deletedCount: 0, cutoffDate: cutoffIso };
  }

  const batch = writeBatch(db);
  snapshot.docs.forEach((docSnap) => {
    batch.delete(docSnap.ref);
  });

  await batch.commit();
  return { deletedCount: snapshot.docs.length, cutoffDate: cutoffIso };
}

/**
 * Background Automatic Retention Check
 * (Client-side automatic background execution disabled: administrative log retention is handled authoritatively server-side via /api/cron/maintenance)
 */
export async function autoPruneEmailLogsIfDue(_retentionDays = DEFAULT_RETENTION_DAYS): Promise<number> {
  // Retention cleanup is an administrative task managed securely on the server side (/api/cron/maintenance).
  // Automatic execution from unprivileged client browser sessions is disabled to avoid permission errors and redundant reads/writes.
  return 0;
}

interface DispatchOptions {
  toEmail: string;
  recipientName?: string;
  templateType: EmailTemplateType;
  subject: string;
  html: string;
  text: string;
  relatedReservationId?: string;
  relatedReservationCode?: string;
  relatedMessageId?: string;
  volunteerName?: string;
  settings?: EmailSettings;
  hasAttachment?: boolean;
}

/**
 * Application-Level SMTP Burst Protection:
 * Prevents multiple simultaneous outbound SMTP requests from opening concurrent sockets.
 * Serializes dispatch calls through an in-memory sequential promise chain with a safe 600ms
 * courteous spacing interval between successive outbound connections to protect ISP rate limits.
 */
let emailDispatchQueue: Promise<any> = Promise.resolve();
const MIN_DISPATCH_INTERVAL_MS = 600;

/**
 * Core Dispatch Function: Proxies email sending via server and logs lean metadata in Firestore.
 * CRITICAL FIREBASE FIRST OPTIMIZATION:
 * - NO full email HTML stored
 * - NO full message body stored
 * - NO attachments or large API response blobs
 * - Errors stored ONLY if delivery fails
 */
async function executeDispatchEmail(options: DispatchOptions): Promise<{ success: boolean; deliveryStatus: string; logId: string; error?: string }> {
  const settings = options.settings || DEFAULT_EMAIL_SETTINGS;
  const newLogRef = doc(collection(db, LOGS_COLLECTION));
  const logId = newLogRef.id;

  const resolvedFromEmail = settings.fromEmail || settings.senderEmail || 'bert.boyden@virginmedia.com';
  const resolvedFromName = settings.fromName || settings.senderName || 'Uniform Loop Immingham';
  const resolvedReplyTo = settings.replyToAddress || settings.replyToEmail || 'bert.boyden@virginmedia.com';

  let isSuccess = false;
  let errorCode: string | undefined = undefined;
  let errorMessage: string | undefined = undefined;

  const maskEmailSafe = (e?: string) => {
    if (!e || !e.includes('@')) return '***';
    const [local, domain] = e.split('@');
    return `${local.slice(0, 2)}***@${domain}`;
  };

  console.log(`[EMAIL TIMING] email dispatch requested at ${new Date().toISOString()} (recipient: ${maskEmailSafe(options.toEmail)}, template: ${options.templateType})`);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (auth.currentUser) {
      try {
        const idToken = await auth.currentUser.getIdToken();
        if (idToken) {
          headers['Authorization'] = `Bearer ${idToken}`;
        }
      } catch (tokenErr) {
        console.warn('Could not retrieve auth token for email dispatch:', tokenErr);
      }
    }

    const baseOrigin = typeof window !== 'undefined' ? '' : (process.env.APP_URL || 'http://localhost:3000');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 25000);

    const res = await fetch(`${baseOrigin}/api/send-email`, {
      method: 'POST',
      headers,
      signal: controller.signal,
      body: JSON.stringify({
        toEmail: options.toEmail,
        recipientName: options.recipientName,
        subject: options.subject,
        html: options.html,
        text: options.text
      })
    }).finally(() => clearTimeout(timeoutId));

    const contentType = res.headers.get('content-type') || '';
    let data: any = null;
    if (contentType.includes('application/json')) {
      data = await res.json().catch(() => null);
    }

    if (res.ok && data?.success) {
      isSuccess = true;
    } else {
      isSuccess = false;
      errorCode = data?.code || 'SMTP_REJECTED';
      errorMessage = data?.error || (res.ok ? 'Email dispatch failed' : `Server responded with status ${res.status}`);
    }
  } catch (err: any) {
    console.error('Email dispatch network error:', err);
    isSuccess = false;
    errorCode = 'NETWORK_ERROR';
    errorMessage = err.message || 'Network exception while dispatching email';
  }

  // MINIMAL ESSENTIAL METADATA SCHEMA ONLY
  // Storing only ~150 bytes per email instead of 25,000+ bytes of HTML!
  const rawLog: EmailLog = {
    id: logId,
    sentAt: new Date().toISOString(),
    recipientEmail: options.toEmail.trim().toLowerCase(),
    recipientName: options.recipientName?.trim() || '',
    templateUsed: options.templateType,
    subject: options.subject.trim(),
    status: isSuccess ? 'Sent' : 'Failed',
    sentBy: options.volunteerName?.trim() || 'System (SMTP)',
    reservationId: options.relatedReservationId,
    reservationCode: options.relatedReservationCode,
    hasAttachment: Boolean(options.hasAttachment),
    // Only store diagnostic errors on failure
    ...(isSuccess ? {} : { errorCode, errorMessage }),
    // Compatibility fields
    deliveryStatus: isSuccess ? 'sent' : 'failed',
    recipient: options.toEmail.trim().toLowerCase(),
    templateType: options.templateType
  };

  try {
    await setDoc(newLogRef, sanitizeFirestoreData(rawLog));
    console.log(`[EMAIL TIMING] Delivery History written at ${new Date().toISOString()} (logId: ${logId}, status: ${rawLog.status})`);
  } catch (logErr) {
    console.error('Failed to record lightweight email audit log:', logErr);
  }

  return {
    success: isSuccess,
    deliveryStatus: isSuccess ? 'sent' : 'failed',
    logId,
    error: errorMessage
  };
}

/**
 * Serialized Dispatch Wrapper:
 * Enforces in-memory sequencing and safe spacing for all outbound client emails,
 * preventing bursts of concurrent SMTP socket connections from exceeding rate limits.
 */
async function dispatchEmail(options: DispatchOptions): Promise<{ success: boolean; deliveryStatus: string; logId: string; error?: string }> {
  const currentTask = (async () => {
    // Wait for any currently queued dispatch to complete before initiating this one
    await emailDispatchQueue.catch(() => {});
    return await executeDispatchEmail(options);
  })();

  // Chain safe inter-message interval after this dispatch completes (success or failure)
  emailDispatchQueue = currentTask.then(
    () => new Promise((resolve) => setTimeout(resolve, MIN_DISPATCH_INTERVAL_MS)),
    () => new Promise((resolve) => setTimeout(resolve, MIN_DISPATCH_INTERVAL_MS))
  );

  return currentTask;
}

/**
 * Trigger Reservation Notification Email
 */
export async function sendReservationNotificationEmail(
  type: 'created' | 'being_prepared' | 'ready' | 'collected' | 'cancelled',
  reservation: Reservation,
  settings: EmailSettings,
  siteSettings: SiteSettings,
  volunteerName?: string
) {
  const code = reservation.reservationCode || reservation.reservationNumber;
  let template: { subject: string; html: string; text: string };
  let templateType: EmailTemplateType;
  let customTemplateId: string | null = null;

  if (type === 'created') {
    templateType = 'reservation_created';
    customTemplateId = 'reservation_created';
    template = renderReservationCreatedEmail(reservation, settings, siteSettings);
  } else if (type === 'being_prepared') {
    templateType = 'reservation_created';
    customTemplateId = 'order_prepared';
    template = renderBeingPreparedEmail(reservation, settings, siteSettings);
  } else if (type === 'ready') {
    templateType = 'ready_for_collection';
    customTemplateId = 'ready_for_collection';
    template = renderReadyForCollectionEmail(reservation, settings, siteSettings);
  } else if (type === 'collected') {
    templateType = 'ready_for_collection';
    template = renderCollectedEmail(reservation, settings);
  } else {
    templateType = 'reservation_cancelled';
    customTemplateId = 'reservation_cancelled';
    template = renderReservationCancelledEmail(reservation, settings);
  }

  // Check if administrator has customized this template in Firestore
  if (customTemplateId) {
    try {
      const customTpl = await fetchCustomTemplate(customTemplateId);
      if (customTpl && customTpl.active !== false && customTpl.subject && customTpl.body) {
        // Build items summary for {RESERVATION_DETAILS}
        const itemsSummary = (reservation.items || [])
          .map((i) => `• ${i.productName} (Size: ${i.size || 'Standard'}) × ${i.quantity || 1}`)
          .join('\n');

        const variables: Record<string, string> = {
          '{CUSTOMER_NAME}': reservation.parentName || 'Parent',
          '{CUSTOMER_EMAIL}': reservation.email || '',
          '{REFERENCE_CODE}': code,
          '{COLLECTION_DATE}': reservation.preferredCollectionDay || 'Scheduled Collection Day',
          '{COLLECTION_VENUE}': siteSettings.collectionLocation || 'Bert Boyden Community Centre',
          '{COLLECTION_ADDRESS}': siteSettings.collectionAddress || '34 Carver Road, Immingham DN40 1DS',
          '{OPENING_HOURS}': siteSettings.openingHours || 'Tue, Thu, Sat 10:00 - 14:00',
          '{RESERVATION_DETAILS}': itemsSummary || 'No specific items recorded',
          '{VOLUNTEER_NAME}': volunteerName || 'Volunteer Team'
        };

        const customSubject = interpolateVariables(customTpl.subject, variables);
        const customBodyText = interpolateVariables(customTpl.body, variables);
        const customBodyHtml = convertBodyToHtml(customBodyText);
        const fullCustomHtml = getBaseLayout(customBodyHtml, settings);

        template = {
          subject: customSubject,
          html: fullCustomHtml,
          text: customBodyText
        };
      }
    } catch (e) {
      console.warn('Failed to load custom template, proceeding with system default renderer:', e);
    }
  }

  return await dispatchEmail({
    toEmail: reservation.email,
    recipientName: reservation.parentName,
    templateType,
    subject: template.subject,
    html: template.html,
    text: template.text,
    relatedReservationId: reservation.id,
    relatedReservationCode: code,
    volunteerName: volunteerName || 'System',
    settings,
    hasAttachment: false
  });
}

/**
 * Trigger Contact Us Form Acknowledgement Email
 */
export async function sendContactAcknowledgementEmail(
  customerName: string,
  toEmail: string,
  refNumber: string,
  userMessage: string,
  relatedMessageId: string,
  settings: EmailSettings
) {
  const template = renderContactAcknowledgementEmail(
    customerName,
    refNumber,
    userMessage,
    settings
  );

  return await dispatchEmail({
    toEmail,
    recipientName: customerName,
    templateType: 'contact_acknowledgement',
    subject: template.subject,
    html: template.html,
    text: template.text,
    relatedMessageId,
    settings,
    hasAttachment: false
  });
}

/**
 * Trigger Volunteer Reply Email to Customer Message
 */
export async function sendVolunteerReplyEmail(
  customerName: string,
  toEmail: string,
  volunteerName: string,
  relatedMessageId: string,
  replyBody: string,
  originalSubject: string,
  settings: EmailSettings,
  replyUrl?: string,
  referenceNumber?: string
) {
  let template = renderVolunteerReplyEmail(
    customerName,
    volunteerName,
    replyBody,
    originalSubject,
    settings,
    replyUrl,
    referenceNumber
  );

  try {
    const customTpl = await fetchCustomTemplate('volunteer_reply');
    if (customTpl && customTpl.active !== false && customTpl.subject && customTpl.body) {
      const variables: Record<string, string> = {
        '{CUSTOMER_NAME}': customerName || 'Parent',
        '{REFERENCE_CODE}': referenceNumber || relatedMessageId || 'UL-MSG',
        '{VOLUNTEER_NAME}': volunteerName || 'Volunteer Team',
        '{MESSAGE_CONTENT}': replyBody
      };

      const customSubject = interpolateVariables(customTpl.subject, variables);
      const customBodyText = interpolateVariables(customTpl.body, variables);
      const customBodyHtml = convertBodyToHtml(customBodyText);
      const fullCustomHtml = getBaseLayout(customBodyHtml, settings);

      template = {
        subject: customSubject,
        html: fullCustomHtml,
        text: customBodyText
      };
    }
  } catch (e) {
    console.warn('Failed to load custom volunteer_reply template, proceeding with system default:', e);
  }

  return await dispatchEmail({
    toEmail,
    recipientName: customerName,
    templateType: 'volunteer_reply',
    subject: template.subject,
    html: template.html,
    text: template.text,
    relatedMessageId,
    volunteerName,
    settings,
    hasAttachment: false
  });
}

/**
 * Trigger Collection Date Decision Email
 */
export async function sendCollectionDateUpdatedEmail(
  reservation: Reservation,
  decision: 'Approved' | 'Declined' | 'Rejected',
  decisionNotes: string | undefined,
  settings: EmailSettings,
  volunteerName?: string
) {
  const code = reservation.reservationCode || reservation.reservationNumber;
  let template = renderCollectionDateUpdatedEmail(reservation, decision, decisionNotes, settings);

  try {
    const customTpl = await fetchCustomTemplate('collection_date_updated');
    if (customTpl && customTpl.active !== false && customTpl.subject && customTpl.body) {
      const variables: Record<string, string> = {
        '{CUSTOMER_NAME}': reservation.parentName || 'Parent',
        '{REFERENCE_CODE}': code,
        '{COLLECTION_DATE}': reservation.preferredCollectionDay || 'Scheduled Collection Date',
        '{DECISION}': decision,
        '{DECISION_LOWER}': decision.toLowerCase(),
        '{VOLUNTEER_NOTE}': decisionNotes ? `Volunteer Note: "${decisionNotes}"` : ''
      };

      const customSubject = interpolateVariables(customTpl.subject, variables);
      const customBodyText = interpolateVariables(customTpl.body, variables);
      const customBodyHtml = convertBodyToHtml(customBodyText);
      const fullCustomHtml = getBaseLayout(customBodyHtml, settings);

      template = {
        subject: customSubject,
        html: fullCustomHtml,
        text: customBodyText
      };
    }
  } catch (e) {
    console.warn('Failed to load custom collection_date_updated template, proceeding with system default:', e);
  }

  return await dispatchEmail({
    toEmail: reservation.email,
    recipientName: reservation.parentName,
    templateType: 'collection_date_updated',
    subject: template.subject,
    html: template.html,
    text: template.text,
    relatedReservationId: reservation.id,
    relatedReservationCode: code,
    volunteerName: volunteerName || 'System',
    settings,
    hasAttachment: false
  });
}

/**
 * Trigger Test Email
 */
export async function sendTestEmail(
  toEmail: string,
  settings: EmailSettings
) {
  const template = renderTestEmail(toEmail, settings);

  return await dispatchEmail({
    toEmail,
    recipientName: 'Volunteer Tester',
    templateType: 'test_email',
    subject: template.subject,
    html: template.html,
    text: template.text,
    volunteerName: 'Volunteer Hub Test',
    settings,
    hasAttachment: false
  });
}

/**
 * Generate Dynamic Email Template Preview on the fly for UI Inspection
 * Eliminates storing HTML strings in Firestore while giving volunteers a complete visual preview!
 */
export function getDynamicTemplatePreview(
  templateType: string,
  recipientName = 'Parent / Customer',
  refCode = 'UL-RES-PREVIEW',
  subject = 'Uniform Loop Notification'
): { templateTitle: string; previewHtml: string; plainDescription: string } {
  const friendlyName = recipientName || 'Parent / Carer';
  const displayCode = refCode || 'UL-REF-000000';

  let templateTitle = 'Standard Email Template';
  let plainDescription = 'Standard automated transactional notification.';

  switch (templateType) {
    case 'reservation_created':
      templateTitle = '1. Reservation Confirmation';
      plainDescription = 'Dispatched immediately when a reservation is placed, including the reservation reference and booked collection slot.';
      break;
    case 'order_prepared':
      templateTitle = '2. Order Being Prepared';
      plainDescription = 'Sent when volunteers begin gathering and inspecting requested items at Bert Boyden Centre.';
      break;
    case 'ready_for_collection':
      templateTitle = '3. Ready for Collection';
      plainDescription = 'Sent when volunteers pack the items and mark the reservation as ready at Bert Boyden Community Centre.';
      break;
    case 'reservation_cancelled':
      templateTitle = '3. Reservation Cancelled';
      plainDescription = 'Sent on reservation cancellation; items are safely restored to the live catalogue stock.';
      break;
    case 'collection_date_updated':
      templateTitle = '4. Collection Date Update';
      plainDescription = 'Sent when a date change request is reviewed and approved/rescheduled by the volunteer team.';
      break;
    case 'contact_acknowledgement':
      templateTitle = '5. Contact Enquiry Acknowledgement';
      plainDescription = 'Instant confirmation sent to customer containing their unique enquiry tracking reference.';
      break;
    case 'volunteer_reply':
      templateTitle = '6. Volunteer Direct Reply';
      plainDescription = 'Personal response composed by an authorized volunteer to answer a community query.';
      break;
    case 'test_email':
      templateTitle = '7. SMTP Diagnostic Test';
      plainDescription = 'Verification ping ensuring outgoing SMTP credentials and port connections are active.';
      break;
    default:
      templateTitle = templateType.replace(/_/g, ' ').toUpperCase();
      break;
  }

  const previewHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; background: #ffffff;">
      <div style="background: #002366; padding: 24px; text-align: center; color: #ffffff;">
        <h1 style="margin: 0; font-size: 20px; font-weight: 800; letter-spacing: -0.5px;">UNIFORM LOOP IMMINGHAM</h1>
        <p style="margin: 4px 0 0; font-size: 12px; color: #93c5fd;">Community Uniform Bank • Bert Boyden Centre</p>
      </div>
      <div style="padding: 24px; color: #1e293b; font-size: 13px; line-height: 1.6;">
        <p style="font-weight: 700; font-size: 15px; margin-top: 0;">Dear ${friendlyName},</p>
        <div style="padding: 12px 16px; background: #f8fafc; border-left: 4px solid #002366; border-radius: 6px; margin: 16px 0;">
          <span style="font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase;">Reference Code</span><br/>
          <strong style="font-size: 14px; color: #002366; font-family: monospace;">${displayCode}</strong>
        </div>
        <p><strong>Subject:</strong> ${subject}</p>
        <p style="color: #475569;">${plainDescription}</p>
        <div style="margin-top: 20px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #64748b;">
          <strong>Collection Venue:</strong> Bert Boyden Community Centre, Immingham<br/>
          <strong>Enquiries:</strong> bert.boyden@virginmedia.com
        </div>
      </div>
      <div style="background: #f1f5f9; padding: 12px; text-align: center; font-size: 11px; color: #64748b;">
        Free Community School Uniform Support for Immingham Families
      </div>
    </div>
  `;

  return { templateTitle, previewHtml, plainDescription };
}

export interface VolunteerNotificationPayload {
  eventType: 'new_order' | 'date_change_request' | 'order_update' | 'new_message';
  reservation?: Reservation;
  updateTitle?: string;
  updateDetails?: string;
  customerMessage?: CustomerMessage;
  settings?: EmailSettings;
  portalUrl?: string;
}

/**
 * Dispatches notification emails to eligible and subscribed volunteers.
 * Uses secure server-side endpoint /api/notifications/dispatch with automatic client-side fallback.
 */
export async function dispatchVolunteerEventNotification(
  payload: VolunteerNotificationPayload
): Promise<{ success: boolean; dispatchedCount: number; recipients?: string[] }> {
  try {
    // 1. Try server-side endpoint
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (auth.currentUser) {
      try {
        const idToken = await auth.currentUser.getIdToken();
        if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
      } catch {}
    }

    const baseOrigin = typeof window !== 'undefined' ? '' : (process.env.APP_URL || 'http://localhost:3000');
    const res = await fetch(`${baseOrigin}/api/notifications/dispatch`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        eventType: payload.eventType,
        reservation: payload.reservation,
        updateTitle: payload.updateTitle,
        updateDetails: payload.updateDetails,
        customerMessage: payload.customerMessage,
        portalUrl: payload.portalUrl || (typeof window !== 'undefined' ? window.location.origin : 'https://together-uniforms.pages.dev/')
      })
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        const returnedRecipients = data.recipients || (data.results ? data.results.map((r: any) => r.email) : []);
        return {
          success: true,
          dispatchedCount: data.count ?? data.dispatchedCount ?? 0,
          recipients: returnedRecipients
        };
      } else {
        console.warn('/api/notifications/dispatch returned success:false:', data.error);
      }
    } else {
      const errText = await res.text().catch(() => '');
      console.warn(`[Volunteer Notif Client] /api/notifications/dispatch error HTTP ${res.status}:`, errText);
    }
  } catch (err) {
    console.warn('[Volunteer Notif Client] Backend endpoint call failed, applying client fallback:', err);
  }

  // 2. Client-side fallback if backend unavailable
  try {
    const requiredPermission = (payload.eventType === 'new_order' || payload.eventType === 'date_change_request' || payload.eventType === 'order_update')
      ? 'reservations'
      : 'messages';

    // Query users with the required permission, administrative role, or standard user/volunteer roles (bounded to prevent quota exhaustion)
    const [permSnap, adminSnap, standardSnap] = await Promise.all([
      getDocs(query(
        collection(db, 'users'),
        where('permissions', 'array-contains', requiredPermission),
        limit(50)
      )).catch((err) => {
        console.warn('[Volunteer Notif Client] Permission query fallback note:', err?.message || err);
        return null;
      }),
      getDocs(query(
        collection(db, 'users'),
        where('role', 'in', ['super_admin', 'admin']),
        limit(10)
      )).catch((err) => {
        console.warn('[Volunteer Notif Client] Admin query fallback note:', err?.message || err);
        return null;
      }),
      getDocs(query(
        collection(db, 'users'),
        where('role', 'in', ['standard_user', 'volunteer']),
        limit(50)
      )).catch((err) => {
        console.warn('[Volunteer Notif Client] Standard users query fallback note:', err?.message || err);
        return null;
      })
    ]);

    const userMap = new Map<string, UserProfile>();
    if (permSnap) {
      permSnap.docs.forEach((d) => userMap.set(d.id, { uid: d.id, ...d.data() } as UserProfile));
    }
    if (adminSnap) {
      adminSnap.docs.forEach((d) => userMap.set(d.id, { uid: d.id, ...d.data() } as UserProfile));
    }
    if (standardSnap) {
      standardSnap.docs.forEach((d) => userMap.set(d.id, { uid: d.id, ...d.data() } as UserProfile));
    }

    // Default fallback designated admin if no users returned
    if (userMap.size === 0) {
      userMap.set('admin-matthew', {
        uid: 'admin-matthew',
        email: 'mrpostie1979@gmail.com',
        displayName: 'Matthew Watson (Super Admin)',
        role: 'super_admin',
        status: 'active',
        permissions: ['reservations', 'messages', 'users', 'emails', 'stock', 'settings'],
        notificationPreferences: {
          newOrder: true,
          orderUpdates: true,
          newCustomerMessage: true
        }
      } as UserProfile);
    }

    const users = Array.from(userMap.values());
    const eligibleUsers: UserProfile[] = [];

    for (const u of users) {
      const prefKey = payload.eventType === 'new_order'
        ? 'newOrders'
        : (payload.eventType === 'date_change_request' || payload.eventType === 'order_update'
            ? 'dateChangeRequests'
            : 'customerMessages');
      const isEligible = isUserEligibleAndSubscribed(u, prefKey as any);

      if (isEligible) {
        eligibleUsers.push(u);
      }
    }

    if (eligibleUsers.length === 0) {
      return { success: true, dispatchedCount: 0 };
    }

    const settings = payload.settings || DEFAULT_EMAIL_SETTINGS;
    const dispatched: string[] = [];

    for (const user of eligibleUsers) {
      if (!user.email) continue;
      let rendered: { subject: string; html: string; text: string } | null = null;
      let templateType: EmailTemplateType = 'volunteer_new_order';

      if (payload.eventType === 'new_order' && payload.reservation) {
        rendered = renderVolunteerNewOrderEmail(payload.reservation, settings, user.displayName || 'Volunteer');
        templateType = 'volunteer_new_order';
      } else if ((payload.eventType === 'date_change_request' || payload.eventType === 'order_update') && payload.reservation) {
        rendered = renderVolunteerOrderUpdateEmail(
          payload.reservation,
          payload.updateTitle || 'Collection Date Change Requested',
          payload.updateDetails || `Customer requested change of collection date to "${payload.reservation.collectionChangeRequest?.requestedDate || payload.reservation.preferredCollectionDay}".`,
          settings,
          user.displayName || 'Volunteer'
        );
        templateType = 'volunteer_order_update';
      } else if (payload.eventType === 'new_message' && payload.customerMessage) {
        rendered = renderVolunteerNewMessageEmail(payload.customerMessage, settings, user.displayName || 'Volunteer');
        templateType = 'volunteer_new_message';
      }

      if (rendered) {
        const sendResult = await dispatchEmail({
          toEmail: user.email,
          recipientName: user.displayName || 'Volunteer',
          templateType,
          subject: rendered.subject,
          html: rendered.html,
          text: rendered.text,
          relatedReservationId: payload.reservation?.id,
          relatedReservationCode: payload.reservation?.reservationCode || payload.reservation?.reservationNumber,
          relatedMessageId: payload.customerMessage?.id,
          volunteerName: 'System Volunteer Notification',
          settings
        });

        if (!sendResult.success && sendResult.error) {
          console.warn(`[Volunteer Notif Client] Failed dispatching to ${user.email}:`, sendResult.error);
        }

        if (sendResult.success) {
          dispatched.push(user.email);
        }
      }
    }

    return { success: true, dispatchedCount: dispatched.length, recipients: dispatched };
  } catch (clientErr) {
    console.error('[Volunteer Notif Client] Direct dispatch fallback failed:', clientErr);
    return { success: false, dispatchedCount: 0 };
  }
}
